<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Dashboard" icon="fa-home"/>

            <div class="start fadeIn">
                <div class="welcome">
                    <h1><strong>{{ welcome }},</strong> {{ currentUser.first_name }}</h1>
                    <h3>What can we help you with today?</h3>
                </div>

                <dashboard-banners/>

                <attention-list v-if="showAttention" :items="attentionItems" :loading="!dashboardFeed && dashboardLoading" class="mb-6"/>

                <div class="columns is-variable is-5 dashboard-columns">
                    <div class="column main-col">
                        <getting-started v-if="isEmpty" class="mb-6"/>
                        <continue-section v-else-if="showContinue" class="mb-6"/>
                        <conference-section class="mb-6"/>
                        <!-- The professional-development pitch; the section
                             itself carries the detail -->
                        <pd-hero v-if="dashboardPd && pdOn" class="mb-6"/>
                        <certification-off-notice v-else-if="dashboardPd && certificationsLoaded && pdHasActivity" :certification-id="1" class="mb-6">
                            You have courses that offer Washington clock hours. Turn on clock hours to track them here.
                        </certification-off-notice>
                        <coaching-section v-if="dashboardCoaching && wiaaOn" class="mb-6"/>
                        <certification-off-notice v-else-if="dashboardCoaching && dashboardCoaching.certificates_count" :certification-id="2" class="mb-6">
                            You have WIAA certificates on file. Turn on WIAA to see your coaching certification here.
                        </certification-off-notice>
                    </div>
                    <div class="column side-col">
                        <maximize-promo v-if="pdOn" class="mb-6"/>
                        <study-activity-mini class="mb-6"/>
                        <payment-plan-panel class="mb-6"/>
                    </div>
                </div>
            </div>
        </template>
    </framework>
</template>

<script>
    import {mapActions, mapGetters} from "vuex";
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import DashboardBanners from "../../components/dashboard/DashboardBanners";
    import GettingStarted from "../../components/dashboard/GettingStarted";
    import AttentionList from "../../components/dashboard/AttentionList";
    import ContinueSection from "../../components/dashboard/ContinueSection";
    import ConferenceSection from "../../components/dashboard/ConferenceSection";
    import PaymentPlanPanel from "../../components/dashboard/PaymentPlanPanel";
    import StudyActivityMini from "../../components/dashboard/StudyActivityMini";
    import PdHero from "../../components/dashboard/PdHero";
    import CertificationOffNotice from "../../components/dashboard/CertificationOffNotice";
    import CoachingSection from "../../components/dashboard/CoachingSection";
    import MaximizePromo from "../../components/pd/MaximizePromo";

    export default {
        components: {
            Framework, SidePanelNav, MainHeader, DashboardBanners, GettingStarted, AttentionList,
            ContinueSection, ConferenceSection, PaymentPlanPanel, StudyActivityMini, PdHero, CertificationOffNotice, CoachingSection, MaximizePromo,
        },
        computed: {
            ...mapGetters(['currentUser', 'enrollments', 'registrations', 'dashboardFeed', 'dashboardLoading', 'attentionItems', 'activeEnrollmentCount', 'continueEnrollments', 'dashboardPd', 'dashboardCoaching', 'clockHoursOn', 'wiaaOn', 'certificationsLoaded']),
            // The PD surfaces wait for the switches to load rather than
            // assuming on: they're too prominent to flash for a coach
            pdOn() {
                return this.certificationsLoaded && this.clockHoursOn;
            },
            welcome: function() {
                const currentHour = new Date().getHours();
                return currentHour >= 4 && currentHour < 12 ? 'Good morning' :
                    currentHour >= 12 && currentHour <= 17 ? 'Good afternoon' :
                        currentHour > 17 || currentHour < 4 ? 'Good evening' : 'Welcome';
            },
            loaded() {
                return Array.isArray(this.enrollments) && Array.isArray(this.registrations);
            },
            isEmpty() {
                return this.loaded && this.enrollments.length === 0 && this.registrations.length === 0;
            },
            // Skeleton while loading; afterwards only when there is an
            // active course to continue (no "nothing in progress" filler)
            showContinue() {
                return !this.loaded || this.continueEnrollments.length > 0;
            },
            // Only when there is something in it — no "all caught up" filler
            showAttention() {
                return this.attentionItems.length > 0;
            },
            // Clock-hour work exists on the account while the certification
            // is switched off: earned hours or courses in progress
            pdHasActivity() {
                const h = this.dashboardPd && this.dashboardPd.hours;
                return !!(h && ((h.earned_total || 0) > 0 || (h.in_progress && (h.in_progress.count || 0) > 0)));
            }
        },
        mounted() {
            if (!localStorage.getItem('token')) this.$router.push('/');
            // Everything fresh on every visit — this is the landing page after
            // signing at TPO, checking out, or finishing a course
            this.getBillingState();
            this.getEnrollments();
            this.getRegistrations();
            this.getDashboard().catch(() => {});
            // A fresh login lands here without the boot loads in App.vue
            // having run (no token at mount), so fetch the switches now
            if (!this.certificationsLoaded) this.getCertifications().catch(() => {});
        },
        methods: {
            ...mapActions(['getBillingState', 'getEnrollments', 'getRegistrations', 'getDashboard', 'getCertifications']),
        }
    };
</script>

<style lang="scss" scoped>
    // Two columns only on wide windows: with the 360px side panel open, the
    // 1/3 column is too cramped for the activity grid below ~1600px, so the
    // sections stack until then.
    .dashboard-columns {
        display: block;

        @media (min-width: 1600px) {
            display: flex;

            .main-col {
                flex: none;
                width: 66.6667%;
            }

            .side-col {
                flex: none;
                width: 33.3333%;
            }
        }
    }

    .start {
        display: flex;
        flex-direction: column;

        transition-delay: 0ms;
        transition-duration: 500ms;

        .welcome {
            color: white;
            margin: 3rem 0;

            h1 {
                font-size: 2.5rem;
                color: #ffffff;

                strong {
                    color: #ffffff;
                }
            }

            h3 {
                font-size: 1.2rem;
                color: rgba(255, 255, 255, 0.7);
            }

            @media (max-width: 600px) {
                margin: 1rem 0 2rem;

                h1 {
                    font-size: 1.7rem;
                }
                h3 {
                    font-size: 1rem;
                }
            }
        }
    }
</style>
